import { useMemo } from 'react';

import { Paragraph, VStack, tv } from '@eluve/components';

import { CopyRichTextButton } from '../CopyRichTextButton';
import { InterimResultLoading } from '../InterimResultLoading';

import { ChatMarkdown } from './ChatMarkdown';
import { Citation, CitationText } from './CitationText';
import { StartingChatMessage } from './StartingChatMessageCard';

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'data';
  content: string;
  isStreaming?: boolean;
  citations?: Citation[];
};

type ChatMessageBubbleProps = {
  message: ChatMessage;
  isStreaming?: boolean;
  templateMessages?: StartingChatMessage[];
  onCopy?: (message: ChatMessage) => void;
};

const chatMessageStyles = tv({
  base: 'group relative break-words rounded-3xl p-4',
  variants: {
    role: {
      user: 'w-auto max-w-[80%] self-end bg-bg-secondary text-text-primary',
      assistant: 'w-full self-start text-text-primary',
      system: 'hidden',
      data: 'hidden',
    },
  },
});

export const ChatMessageBubble = ({
  message,
  isStreaming,
  templateMessages,
  onCopy,
}: ChatMessageBubbleProps) => {
  const hasCitations = message.citations && message.citations.length > 0;
  // Filter citations to ensure valid indices
  const validCitations =
    hasCitations && message.citations
      ? message.citations.filter(
          (citation) =>
            typeof citation.startIndex === 'number' &&
            typeof citation.endIndex === 'number',
        )
      : [];

  const userMessageContent = useMemo(() => {
    if (message.role !== 'user') return null;

    // If the message matches a starting message template, display its title instead
    const matchingTemplate = templateMessages?.find(
      (template) =>
        template.content.replace(/ +/g, ' ').trim() ===
        message.content.replace(/ +/g, ' ').trim(),
    );

    if (matchingTemplate) {
      return matchingTemplate.title;
    }

    // Otherwise, render the original message content with line breaks
    return message.content.split('\n').map((line, index, splitMessage) => (
      <span key={index}>
        {line}
        {index < splitMessage.length - 1 && <br />}
      </span>
    ));
  }, [message.content, message.role, templateMessages]);

  return (
    <VStack className={chatMessageStyles({ role: message.role })}>
      {message.role === 'assistant' && (
        <div className="absolute right-0.5 top-0.5 opacity-0 transition-opacity duration-200 ease-linear group-hover:opacity-100">
          <CopyRichTextButton
            markdownContent={message.content}
            onCopy={() => onCopy?.(message)}
          />
        </div>
      )}
      {message.role === 'user' ? (
        <Paragraph size="md" color="inherit">
          {userMessageContent}
        </Paragraph>
      ) : validCitations.length > 0 ? (
        <CitationText text={message.content} citations={validCitations} />
      ) : (
        <ChatMarkdown>{message.content}</ChatMarkdown>
      )}
      {isStreaming && <InterimResultLoading />}
    </VStack>
  );
};
